import { IconX } from "@tabler/icons-react";
import { PropsWithChildren, ReactNode } from "react";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { SIDE_PANEL_Z_INDEX } from "#app/theme/v2/zIndex";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { Text } from "#components/atoms/Typographies/Text";
import { Title } from "#components/atoms/Typographies/Title";
import { Icon } from "#shared/icons/Icon";
import { Box } from "#shared/view/components/Box";
import { Drawer } from "#shared/view/components/Drawer";
import { Flex } from "#shared/view/components/Flex";

type Props = PropsWithChildren<{
  opened: boolean;
  onClose: () => void;
  size?: number;
  toolbar?: ReactNode;
}>;

type HeaderProps = PropsWithChildren<{
  title: string;
  subtitle?: string;
  onBack?: () => void;
  backText?: string;
  onClose: () => void;
  closeText: string;
}>;

type FooterProps = PropsWithChildren<{
  withFooterBorder?: boolean;
}>;

const DEFAULT_SIZE = 640;

const SidePanel = ({ opened, onClose, size = DEFAULT_SIZE, children }: Props) => (
  <Drawer.Root
    opened={opened}
    onClose={onClose}
    position="right"
    size={size}
    offset={8}
    zIndex={SIDE_PANEL_Z_INDEX}
  >
    <Drawer.Overlay backgroundOpacity={0.34} />
    <Drawer.Content
      style={{
        borderRadius: `${radius.xxxl} ${radius.md} ${radius.md} ${radius.xxxl}`,
      }}
    >
      <Flex direction="column" h="100%">
        {children}
      </Flex>
    </Drawer.Content>
  </Drawer.Root>
);

const SidePanelHeader = ({
  title,
  subtitle,
  onBack,
  backText,
  onClose,
  closeText,
  children,
}: HeaderProps) => {
  return (
    <Flex
      direction="column"
      gap={children ? spacing[6] : 0}
      pl={spacing[8]}
      pr={spacing[4]}
      py={spacing[4]}
    >
      <Flex align="flex-start" justify="space-between">
        {onBack ? (
          <Flex align="flex-start" gap={spacing[4]} pt={spacing[4]}>
            <IconButton variant="secondary" aria-label={backText ?? closeText} onClick={onBack}>
              <Icon name="arrowLeft" size={16} />
            </IconButton>
            <Flex direction="column" gap={spacing[1]}>
              <Title size="small">{title}</Title>
              {subtitle ? (
                <Text size="small" color={colors.text.secondaryDefault}>
                  {subtitle}
                </Text>
              ) : null}
            </Flex>
          </Flex>
        ) : (
          <>
            <Title size="small">{title}</Title>
            <IconButton variant="secondary" aria-label={closeText} onClick={onClose}>
              <IconX size={16} />
            </IconButton>
          </>
        )}
      </Flex>
      {children}
    </Flex>
  );
};

const SidePanelContent = ({ children }: PropsWithChildren) => {
  return (
    <Box flex={1} style={{ overflow: "auto" }} px={spacing[8]} pt={spacing[4]} pb={spacing[4]}>
      {children}
    </Box>
  );
};

const SidePanelFooter = ({ withFooterBorder, children }: FooterProps) => {
  return (
    <Flex
      align="center"
      justify="space-between"
      gap={spacing[3]}
      px={spacing[8]}
      pt={spacing[4]}
      pb={spacing[8]}
      style={{
        borderTop: withFooterBorder ? `1px solid ${colors.border.quaternary}` : "none",
      }}
    >
      {children}
    </Flex>
  );
};

export { SidePanel, SidePanelFooter, SidePanelContent, SidePanelHeader };
